import { formatCadastralNumber, formatMainNumber } from '@utils/cadastral-number-formatter'
import { formatFullName } from '@utils/helper-functions'
import type { CooperativeMembershipDto } from '@/types/CooperativeMembership'
import type { PropertyDetailsDto } from '@/types/PropertyDto'

export interface WarningCopyParams {
  propertyNumber: string
  memberName: string
}

// Cooperative-membership-specific display helpers for warning copy (not generic utils).
export function getCooperativeMemberDisplayName(
  membership: CooperativeMembershipDto,
): string | null {
  const forestOwner = membership.forestOwner
  if (!forestOwner) {
    return null
  }

  return formatFullName(forestOwner.name)
}

export function formatPropertyNumberLabel(
  property: PropertyDetailsDto,
  propertyNumberOverride?: string,
): string | null {
  if (propertyNumberOverride?.trim()) {
    return propertyNumberOverride.trim()
  }

  if (property.cadastralNumber?.trim()) {
    return formatCadastralNumber(property.cadastralNumber)
  }

  if (property.mainNumber) {
    return formatMainNumber(property.mainNumber)
  }

  return null
}

export function formatPropertyNumberFromSearch(
  municipality: string,
  farmNumber: string,
  propertyNumber?: string,
): string {
  const holding = propertyNumber?.trim() || '0'
  return `${municipality}-${farmNumber}/${holding}`
}

export function hasDisplayContext(params: WarningCopyParams | null): params is WarningCopyParams {
  return Boolean(params?.propertyNumber.trim() && params.memberName.trim())
}

export type CooperativeMembershipWarningVariant = 'addProperty' | 'addPropertyGeneric'

export interface CooperativeMembershipWarningCopy {
  title: string
  description: string
}

/**
 * Builds the interpolated title/description copy for the cooperative membership
 * warning modal, shared between the modal body and its trigger (which must know
 * the copy upfront since it's passed as static ModalOptions to useDialog().openModal()).
 */
export function getCooperativeMembershipWarningCopy(
  t: (key: string, params?: Record<string, string>) => string,
  variant: CooperativeMembershipWarningVariant,
  copyParams: WarningCopyParams,
): CooperativeMembershipWarningCopy {
  const translationKey =
    variant === 'addPropertyGeneric'
      ? 'cooperativemembership.warning.addPropertyGeneric'
      : 'cooperativemembership.warning.addProperty'

  const interpolationParams = {
    propertyNumber: copyParams.propertyNumber,
    memberName: copyParams.memberName,
  }

  const title = t(`${translationKey}.title`, interpolationParams)
  const bodyTransferWithCapital = t(
    `${translationKey}.bodyTransferWithCapital`,
    interpolationParams,
  )
  const bodyTransferWithoutCapital = t(
    `${translationKey}.bodyTransferWithoutCapital`,
    interpolationParams,
  )

  return {
    title,
    description: [bodyTransferWithCapital, bodyTransferWithoutCapital].join('\n\n'),
  }
}

export interface PropertyBlockedByShareholderCopyParams {
  propertyNumber: string
  memberName: string
  memberCustomerNumber: string | null
  isGenericWarning: boolean
}

/**
 * Copy for the register-shareholder blocking warning (ADO Bug #3777). Unlike
 * `getCooperativeMembershipWarningCopy`, this flow has no "continue without capital"
 * option — the property can never be added while it belongs to another shareholder's
 * cooperative membership. Falls back to the generic (no member name/customer number)
 * variant when the property resolves to multiple memberships.
 */
export function getPropertyBlockedByShareholderCopy(
  t: (key: string, params?: Record<string, string>) => string,
  copyParams: PropertyBlockedByShareholderCopyParams,
): CooperativeMembershipWarningCopy {
  if (copyParams.isGenericWarning) {
    const interpolationParams = { propertyNumber: copyParams.propertyNumber }
    return {
      title: t(
        'cooperativemembership.warning.propertyBlockedByShareholderGeneric.title',
        interpolationParams,
      ),
      description: t(
        'cooperativemembership.warning.propertyBlockedByShareholderGeneric.body',
        interpolationParams,
      ),
    }
  }

  const interpolationParams = {
    propertyNumber: copyParams.propertyNumber,
    memberName: copyParams.memberName,
    memberCustomerNumber: copyParams.memberCustomerNumber ?? '',
  }

  return {
    title: t(
      'cooperativemembership.warning.propertyBlockedByShareholder.title',
      interpolationParams,
    ),
    description: t(
      'cooperativemembership.warning.propertyBlockedByShareholder.body',
      interpolationParams,
    ),
  }
}
